import { CobaltBanner } from '@cae/cobalt-react/components/banner';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { useModules } from '@contexts/modules-provider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import styles from './MaintenanceBanner.module.css';
import {
  MODULE_TO_FLAG,
  parseMaintenanceBannerFlag,
} from './MaintenanceBanner.utils';

const MaintenanceBanner = React.memo((): React.ReactElement | null => {
  const flags = useFlags();
  const { selectedModule } = useModules();
  const moduleName = selectedModule?.name;

  const banner = React.useMemo(() => {
    if (!moduleName) return parseMaintenanceBannerFlag(undefined);
    const flagName = MODULE_TO_FLAG[moduleName];
    if (!flagName) return parseMaintenanceBannerFlag(undefined);
    return parseMaintenanceBannerFlag(flags[flagName]);
  }, [flags, moduleName]);

  const [isDismissed, setIsDismissed] = React.useState<boolean>(false);

  const linkVariant = banner.severity === 'warning' ? 'primary' : 'inverse';

  const handleClose = React.useCallback((): void => {
    setIsDismissed(true);
  }, []);

  if (!banner.enabled || isDismissed) {
    return null;
  }

  return (
    <CobaltBanner
      variant={banner.severity}
      hasClose={banner.dismissible}
      showIcon
      onClose={handleClose}
      className={styles.maintenanceBanner}
    >
      {banner.message}
      {banner.action && (
        <CobaltLink
          variant={linkVariant}
          href={banner.action.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {banner.action.label}
        </CobaltLink>
      )}
    </CobaltBanner>
  );
});

MaintenanceBanner.displayName = 'MaintenanceBanner';

export { MaintenanceBanner };
